import React, { useEffect, useMemo, useState } from 'react';
import { RadioGroup, FormControlLabel, Radio, TextField, FormHelperText, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { AUDIENCE_OPTIONS } from '../../../../../constants/WelcomeMessageConst';
import CustomMultiSelect from '../../../../../shared/form-controls/custom-multi-select/Custom-Multi-Select';
import CustomChipInput from '../../../../../shared/form-controls/custom-chip-input/Custom-Chip-Input';
import { fetchLovData } from '../../../../../utils/ServicesAPIUtils';
import { handleValidation } from '../../../../../utils/formUtils';

const CORRECT_ORDER = ['Rooms', 'Room Type', 'Block Code', 'Loyalty Tier'];

const splitEmails = (str = '') =>
  str
    ?.split(',')
    ?.map((s) => s.trim())
    ?.filter(Boolean);
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const EmailServiceAudienceTab = ({
  formData,
  handleInputChange,
  setFormData,
  originalRoomList = [],
  originalRoomTypeList = [],
  hotelId,
  language,
  isReadOnly = false,
  hideRecipientToggle = false,
  singleAudienceType = false,
}) => {
  const recipient = formData?.recipientType?.value || 'Guests';
  const [loyaltyTiers, setLoyaltyTiers] = useState([]);
  const [blockCodes, setBlockCodes] = useState([]);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [blockCodeLoading, setBlockCodeLoading] = useState(false);
  const [audienceTypesOrdered, setAudienceTypesOrdered] = useState([]);
  const activeRoomTypes = useMemo(
    () => (originalRoomTypeList || []).filter((rt) => rt.isActive),
    [originalRoomTypeList],
  );
  const activeRooms = useMemo(() => {
    const activeRoomTypeIds = new Set(activeRoomTypes.map((rt) => rt.id));
    return (originalRoomList || []).filter((room) => activeRoomTypeIds.has(room.roomTypeId));
  }, [originalRoomList, activeRoomTypes]);
  const [roomOptions, setRoomOptions] = useState(activeRooms);
  const [roomTypeOptions, setRoomTypeOptions] = useState(activeRoomTypes);
  const [roomSearchValue, setRoomSearchValue] = useState('');
  const [roomTypeSearchValue, setRoomTypeSearchValue] = useState('');

  useEffect(() => {
    const run = async () => {
      if (!hotelId) return;
      setLoyaltyLoading(true);
      try {
        const dataWrapper = await fetchLovData(hotelId, language, 'loyalty');
        console.log('Loyalty Tiers API Response:', dataWrapper);
        if (dataWrapper?.data?.getLov?.length > 0) {
          const first = dataWrapper.data.getLov[0] || {};
          const lst = first.lists?.find((l) => l.listName === 'loyalty');
          console.log('Loyalty List:', lst);
          if (lst) {
            const langData =
              lst.languages?.find((it) => it.lang === language) || lst.languages?.[0];
            console.log('Loyalty Lang Data:', langData, 'Requested lang:', language);
            setLoyaltyTiers(langData?.values || []);
          } else {
            setLoyaltyTiers([]);
          }
        } else {
          setLoyaltyTiers([]);
        }
      } catch (e) {
        console.error('Error fetching loyalty tiers:', e);
        setLoyaltyTiers([]);
      } finally {
        setLoyaltyLoading(false);
      }
    };
    run();
  }, [hotelId, language]);

  useEffect(() => {
    const run = async () => {
      if (!hotelId) return;
      setBlockCodeLoading(true);
      try {
        const dataWrapper = await fetchLovData(hotelId, language, 'blockcodes');
        console.log('Block Codes API Response:', dataWrapper);
        if (dataWrapper?.data?.getLov?.length > 0) {
          const first = dataWrapper.data.getLov[0] || {};
          const lst = first.lists?.find((l) => l.listName === 'blockcodes');
          console.log('Block Code List:', lst);
          if (lst) {
            const langData =
              lst.languages?.find((it) => it.lang === language) || lst.languages?.[0];
            console.log('Block Code Lang Data:', langData, 'Requested lang:', language);
            setBlockCodes(langData?.values || []);
          } else {
            setBlockCodes([]);
          }
        } else {
          setBlockCodes([]);
        }
      } catch (e) {
        console.error('Error fetching block codes:', e);
        setBlockCodes([]);
      } finally {
        setBlockCodeLoading(false);
      }
    };
    run();
  }, [hotelId, language]);

  useEffect(() => {
    const raw = formData?.audienceType?.value || [];
    const sanitized = raw.filter((type) => type !== 'Floors');
    if (sanitized.length !== raw.length) {
      handleInputChange({
        audienceType: {
          ...(formData?.audienceType || { value: [] }),
          value: sanitized,
          errors: {},
        },
      });
      return;
    }
    const selected = sanitized?.map((opt) => (typeof opt === 'string' ? opt : opt?.name || opt));
    const ordered = CORRECT_ORDER?.filter((t) => selected?.includes(t));
    setAudienceTypesOrdered(ordered);

    if (ordered.length > 1) {
      const init = {};
      for (let i = 1; i < ordered.length; i++) {
        const key = ordered[i - 1]?.replaceAll(' ', '');
        if (!formData?.audienceRelations?.value?.[key]) init[key] = 'AND';
      }
      if (Object.keys(init).length) {
        handleInputChange({
          audienceRelations: {
            ...(formData?.audienceRelations || { value: {} }),
            value: { ...(formData?.audienceRelations?.value || {}), ...init },
          },
        });
      }
    }
  }, [formData?.audienceType, formData?.audienceRelations, handleInputChange]);

  useEffect(() => {
    const selected = new Set(formData?.audienceType?.value || []);
    const map = {
      Rooms: 'residentRoom',
      'Room Type': 'roomType',
      'Block Code': 'blockCodes',
      'Loyalty Tier': 'loyaltyTiers',
    };
    Object.entries(map).forEach(([label, key]) => {
      if (
        !selected.has(label) &&
        Array.isArray(formData?.[key]?.value) &&
        formData[key].value.length
      ) {
        handleInputChange({
          [key]: { ...(formData[key] || { value: [] }), value: [] },
        });
      }
    });
  }, [formData?.audienceType?.value]);

  const onRecipientChange = (val) => {
    if (isReadOnly) return;
    handleInputChange({
      recipientType: {
        ...(formData?.recipientType || { value: '' }),
        value: val,
        errors: {},
      },
    });

    if (val === 'Staff') {
      handleInputChange({
        audienceType: { ...(formData?.audienceType || { value: [] }), value: [] },
        residentRoom: { ...(formData?.residentRoom || { value: [] }), value: [] },
        roomType: { ...(formData?.roomType || { value: [] }), value: [] },
        blockCodes: { ...(formData?.blockCodes || { value: [] }), value: [] },
        loyaltyTiers: { ...(formData?.loyaltyTiers || { value: [] }), value: [] },
        audienceRelations: { ...(formData?.audienceRelations || { value: {} }), value: {} },
        staffEmails: { ...(formData?.staffEmails || { value: '' }), value: '', errors: {} },
        staffEmailList: { ...(formData?.staffEmailList || { value: [] }), value: [] },
      });
    }
  };

  const handleRelationChange = (index, value) => {
    const prevKey = `${audienceTypesOrdered[index - 1]?.replaceAll(' ', '')}`;
    const curr = { ...(formData?.audienceRelations?.value || {}) };
    curr[prevKey] = value;
    handleInputChange({
      audienceRelations: { ...(formData?.audienceRelations || { value: {} }), value: curr },
    });
  };

  const setAudienceTypeArray = (vals) => {
    const normalized = Array.isArray(vals) ? vals : vals?.value || [];
    setAudienceTypesOrdered(CORRECT_ORDER.filter((t) => normalized.includes(t)));
    handleInputChange({
      audienceType: { ...(formData?.audienceType || { value: [] }), value: normalized, errors: {} },
    });
  };

  const updateArrayField = (fieldKey, vals) => {
    const normalized = Array.isArray(vals) ? vals : vals?.value || [];
    handleInputChange({
      [fieldKey]: { ...(formData?.[fieldKey] || { value: [] }), value: normalized, errors: {} },
    });
  };

  useEffect(() => {
    setRoomOptions(activeRooms);
  }, [activeRooms]);

  useEffect(() => {
    setRoomTypeOptions(activeRoomTypes);
  }, [activeRoomTypes]);

  const onSearchRoomList = (search) => {
    setRoomSearchValue(search);
    if (!search) {
      setRoomOptions(activeRooms);
      return;
    }
    setRoomOptions(
      activeRooms?.filter((room) =>
        room?.number?.toLowerCase?.()?.includes(search?.toLowerCase()),
      ),
    );
  };

  const onSearchRoomTypeList = (search) => {
    setRoomTypeSearchValue(search);
    if (!search) {
      setRoomTypeOptions(activeRoomTypes);
      return;
    }
    setRoomTypeOptions(
      activeRoomTypes?.filter((roomType) =>
        roomType?.name?.toLowerCase?.()?.includes(search?.toLowerCase()),
      ),
    );
  };

  const audienceSelectOptions = useMemo(
    () => AUDIENCE_OPTIONS.filter((opt) => opt?.name !== 'Floors'),
    [],
  );
  const staffRaw = formData?.staffEmails?.value ?? '';
  const staffErrors = formData?.staffEmails?.errors || {};
  const staffErr = staffErrors.validEmailList || staffErrors.duplicate || '';

  const onStaffEmailsChange = (raw) => {
    const emailField = {
      fieldName: 'Staff Emails',
      validations: ['validEmailList'],
      value: raw,
      errors: {},
    };
    handleValidation(emailField);

    const emails = splitEmails(raw);

    const seen = new Set();
    const duplicates = new Set();
    emails.forEach((email) => {
      const key = email.toLowerCase();
      if (seen.has(key)) {
        duplicates.add(key);
      } else {
        seen.add(key);
      }
    });
    const errors = {};
    if (emailField?.errors?.validEmailList) {
      errors.validEmailList = emailField.errors.validEmailList;
    }
    if (duplicates.size) {
      errors.duplicate = 'Duplicate email addresses are not allowed.';
    }
    handleInputChange({
      staffEmails: {
        ...(formData?.staffEmails || { value: '' }),
        value: raw,
        errors,
      },
      staffEmailList: { ...(formData?.staffEmailList || { value: [] }), value: emails },
    });
  };

  if (isReadOnly) {
    const audienceTypes = audienceTypesOrdered;
    const staffEmailsList = formData?.staffEmailList?.value || [];

    const recipient = formData?.recipientType?.value || 'Guests';
    const recipientLabel = recipient === 'Staff' ? 'Staffs' : recipient;

    const getValuesForType = (type) => {
      switch (type) {
        case 'Rooms':
          return Array.isArray(formData?.residentRoom?.value) ? formData.residentRoom.value : [];
        case 'Room Type':
          return Array.isArray(formData?.roomType?.value) ? formData.roomType.value : [];
        case 'Block Code':
          return Array.isArray(formData?.blockCodes?.value)
            ? formData.blockCodes.value
                .map((id) => {
                  const bc = blockCodes.find((b) => b.id === id || b.name === id);
                  return bc ? bc.name : id;
                })
                .filter((x) => x)
            : [];
        case 'Loyalty Tier':
          return Array.isArray(formData?.loyaltyTiers?.value)
            ? formData.loyaltyTiers.value
                .map((id) => {
                  const t = loyaltyTiers.find((tl) => tl.id === id || tl.name === id);
                  return t ? t.name : id;
                })
                .filter((x) => x)
            : [];
        default:
          return [];
      }
    };

    const getRelationText = (index) => {
      if (index >= audienceTypes.length - 1) return '';
      const key = audienceTypes[index].replaceAll(' ', '');
      return formData?.audienceRelations?.value?.[key] || '';
    };

    return (
      <div className='row px-3 w-100 d-flex mt-4 form-scroll audience-view--container'>
        <div className='row px-3 w-100 d-flex mt-3'>
          <div className='col-md-11 mb-3'>
            <strong>To {recipientLabel}</strong>
          </div>

          {recipient === 'Staff' && (
            <div className='col-md-11 mb-3'>
              <strong>Emails</strong>
              <p>{staffEmailsList?.length > 0 ? staffEmailsList.join(', ') : '-'}</p>
            </div>
          )}
        </div>

        {recipient === 'Guests' &&
          audienceTypes.map((type, idx) => {
            const vals = getValuesForType(type);
            return (
              <React.Fragment key={`${type}-${idx}`}>
                <div className='row mb-3'>
                  <div className='col-md-12 mb-1'>
                    <strong>{type}</strong>
                  </div>
                  <div className='col-md-12'>
                    {type === 'Block Code' || type === 'Loyalty Tier' ? (
                      <div className='d-flex flex-wrap gap-2'>
                        {vals.length > 0 ? (
                          vals.map((val, i) => (
                            <div
                              key={i}
                              className='audience-chip'
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 12px',
                                margin: '2px',
                                border: '1px solid #000',
                                borderRadius: '20px',
                                fontSize: '14px',
                                color: '#333',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {val}
                            </div>
                          ))
                        ) : (
                          <p>-</p>
                        )}
                      </div>
                    ) : (
                      <p>{vals.length > 0 ? vals.join(', ') : '-'}</p>
                    )}
                  </div>
                </div>

                {idx < audienceTypes.length - 1 && getRelationText(idx) && (
                  <div className='audience-relation--divider mb-4'>
                    <span>
                      {getRelationText(idx)
                        ?.toLowerCase()
                        ?.replace(/^./, (c) => c?.toUpperCase())}
                    </span>
                  </div>
                )}
              </React.Fragment>
            );
          })}
      </div>
    );
  }

  return (
    <div className='w-100'>
      {!hideRecipientToggle && (
        <RadioGroup row value={recipient} onChange={(e) => onRecipientChange(e.target.value)}>
          <FormControlLabel value='Guests' control={<Radio color='primary' />} label='Guests' />
          <FormControlLabel value='Staff' control={<Radio color='primary' />} label='Staff' />
        </RadioGroup>
      )}
      {recipient === 'Staff' && (
        <div className='row w-100 mt-3'>
          <div className='col-md-11'>
            <TextField
              fullWidth
              required
              label='To Email Addresses (separate with commas)'
              placeholder='john@hotel.com, jane@brand.com'
              value={staffRaw}
              onChange={(e) => onStaffEmailsChange(e?.target?.value)}
              error={Boolean(staffErr)}
              disabled={isReadOnly}
            />
            {staffErr ? <FormHelperText error>{staffErr}</FormHelperText> : null}
          </div>
        </div>
      )}
      {recipient === 'Guests' && (
        <div className='row w-100 d-flex mt-3'>
          <div className='col-md-6 mb-3'>
            {singleAudienceType ? (
              <FormControl fullWidth variant='standard' error={Boolean(formData?.audienceType?.errors?.required)}>
                <InputLabel required>Select Audience Type</InputLabel>
                <Select
                  value={audienceTypesOrdered[0] || ''}
                  onChange={(e) => setAudienceTypeArray([e.target.value])}
                  disabled={isReadOnly}
                  MenuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, getContentAnchorEl: null }}
                >
                  {audienceSelectOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                  ))}
                </Select>
                {formData?.audienceType?.errors?.required && (
                  <FormHelperText>{formData.audienceType.errors.required}</FormHelperText>
                )}
              </FormControl>
            ) : (
              <CustomMultiSelect
                item={{
                  ...(formData?.audienceType || { value: [] }),
                  fieldName: 'Select Audience Type(s)',
                  validations: isReadOnly ? [] : ['required'],
                  value: audienceTypesOrdered,
                  errors: formData?.audienceType?.errors || {},
                }}
                handleInputChange={setAudienceTypeArray}
                labelField='name'
                valueField='id'
                options={audienceSelectOptions}
                hideNullOption
                isMulti
                placeholder='Select Audience Type(s)'
                isDisabled={isReadOnly}
              />
            )}
          </div>
          {audienceTypesOrdered.map((type, idx) => (
            <React.Fragment key={type}>
              {idx > 0 && (
                <div className='row mb-3'>
                  <div className='col-md-11'>
                    <div className='audience-relation-container d-flex justify-content-around align-items-center'>
                      <RadioGroup
                        row
                        value={
                          formData?.audienceRelations?.value?.[
                            `${audienceTypesOrdered[idx - 1]?.replaceAll(' ', '')}`
                          ] || 'AND'
                        }
                        onChange={(e) => handleRelationChange(idx, e.target.value)}
                        className='mb-0'
                      >
                        <FormControlLabel
                          value='AND'
                          control={<Radio color='primary' />}
                          label='And'
                        />
                        <FormControlLabel
                          value='OR'
                          control={<Radio color='primary' />}
                          label='Or'
                        />
                      </RadioGroup>
                    </div>
                  </div>
                </div>
              )}
              {type === 'Rooms' && (
                <div className='row mb-3'>
                  <div className='col-md-6 mb-3'>
                    <CustomMultiSelect
                      item={{
                        ...(formData?.residentRoom || { value: [] }),
                        fieldName: 'Select Room(s)',
                        validations: isReadOnly ? [] : ['required'],
                        value: Array.isArray(formData?.residentRoom?.value)
                          ? formData.residentRoom.value
                          : [],
                        errors: formData?.residentRoom?.errors || {}
                      }}
                      options={roomOptions}
                      hideNullOption
                      handleInputChange={(e) => updateArrayField('residentRoom', e)}
                      valueField='number'
                      labelField='number'
                      isSearchable
                      getSearchInput={onSearchRoomList}
                      searchValue={roomSearchValue}
                      searchPlaceholder='Search Room(s)'
                      isDisabled={isReadOnly}
                    />
                  </div>
                </div>
              )}

              {type === 'Room Type' && (
                <div className='row mb-3'>
                  <div className='col-md-6 mb-3'>
                    <CustomMultiSelect
                      item={{
                        ...(formData?.roomType || { value: [] }),
                        fieldName: 'Select Room Type(s)',
                        validations: isReadOnly ? [] : ['required'],
                        value: Array.isArray(formData?.roomType?.value)
                          ? formData.roomType.value
                          : [],
                        errors: formData?.roomType?.errors || {}
                      }}
                      options={roomTypeOptions}
                      hideNullOption
                      handleInputChange={(e) => updateArrayField('roomType', e)}
                      valueField='name'
                      labelField='name'
                      isSearchable
                      getSearchInput={onSearchRoomTypeList}
                      searchValue={roomTypeSearchValue}
                      searchPlaceholder='Search Room Type(s)'
                      isDisabled={isReadOnly}
                    />
                  </div>
                </div>
              )}

              {type === 'Block Code' && (
                <div className='row mb-3'>
                  <div className='col-md-11 mb-3'>
                    <CustomChipInput
                      options={blockCodes}
                      labelField='name'
                      textFieldLabel='Block Codes'
                      textFieldPlaceholder='Select Block Code(s)'
                      formData={formData}
                      setFormData={setFormData}
                      formField='blockCodes'
                      value={formData?.blockCodes?.value || []}
                      isCloseOnSelect={false}
                      isClearable={false}
                      validations={isReadOnly ? [] : ['required']}
                      required={!isReadOnly}
                    />
                    {blockCodeLoading && <p className='text-muted mt-2'>Loading block codes...</p>}
                  </div>
                </div>
              )}

              {type === 'Loyalty Tier' && (
                <div className='row mb-3'>
                  <div className='col-md-11 mb-3'>
                    <CustomChipInput
                      options={loyaltyTiers}
                      labelField='name'
                      textFieldLabel='Loyalty Tiers'
                      textFieldPlaceholder='Select Loyalty Tier(s)'
                      formData={formData}
                      setFormData={setFormData}
                      formField='loyaltyTiers'
                      value={formData?.loyaltyTiers?.value || []}
                      isCloseOnSelect={false}
                      isClearable={false}
                      validations={isReadOnly ? [] : ['required']}
                      required={!isReadOnly}
                    />
                    {loyaltyLoading && <p className='text-muted mt-2'>Loading loyalty tiers...</p>}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmailServiceAudienceTab;
